import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded';
import LinkRoundedIcon from '@mui/icons-material/LinkRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { applicationsApi, type ApplicationJourneyHeaderDto } from '../../api/admin';
import type { IdentityLookupRow } from '../../api/identityVerificationDashboardApi';
import { identityVerificationDashboardApi } from '../../api/identityVerificationDashboardApi';
import {
  buildLinkageComparisonFields,
  piiSnapshotsEqual,
  resolveEffectiveApplicantPii,
  resolveLinkageApplicantPiiFromIdentity,
  resolvePendingReviewHeadline,
} from '../../lib/legacyLinkageApplicantPii';
import { LEGACY_LINKAGE_PANEL } from '../../lib/legacyLinkageCopy';
import {
  decisionEnginePowerIdMismatch,
  formatLinkageDistance,
  formatLinkageStamp,
  linkageFundingLabel,
  linkageMatchMethodLabel,
  linkageSourceLabel,
  linkageStatusChipColor,
  linkageStatusLabel,
  mergeLinkageCandidates,
  normalizeLinkageCandidates,
  normalizeLinkageStatus,
  pendingReviewCandidateCount,
  piiMatchLabel,
  resolveLinkedLegacyPii,
  type LegacyLinkageJourney,
} from '../../lib/legacyLinkageDisplay';
import { ApplicationSectionCard } from '../application/applicationSectionChrome';
import { LegacyLinkageCandidateCard } from './LegacyLinkageCandidateCard';

const REVIEW_CARD_MAX = 3;

type Props = {
  application?: ApplicationJourneyHeaderDto | null;
  decisionEnginePowerId?: string | null;
  /** When provided, skips a duplicate identity lookup query (Identity tab source). */
  identityLookupItem?: IdentityLookupRow | null;
};

function CopyValueButton({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <Tooltip title={copied ? 'Copied' : 'Copy'}>
      <IconButton
        size="small"
        aria-label="Copy value"
        onClick={async () => {
          try {
            await navigator.clipboard.writeText(value);
            setCopied(true);
            window.setTimeout(() => setCopied(false), 1500);
          } catch {
            /* ignore */
          }
        }}
      >
        <ContentCopyRoundedIcon fontSize="inherit" />
      </IconButton>
    </Tooltip>
  );
}

export function ApplicationLegacyLinkagePanel({
  application,
  decisionEnginePowerId,
  identityLookupItem,
}: Props) {
  const queryClient = useQueryClient();
  const linkage = application?.legacyLinkage as LegacyLinkageJourney | null | undefined;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const [technicalOpen, setTechnicalOpen] = useState(false);
  const [actionNotice, setActionNotice] = useState<string | null>(null);

  const status = normalizeLinkageStatus(linkage);
  const reviewId = linkage?.reviewId ?? null;
  const pending = status === 'PENDING_REVIEW' && Boolean(reviewId);
  const resolvedLegacyMatch = status === 'RESOLVED' && Boolean(linkage?.powerId) && !linkage?.isMinted;
  const needsIdentityPii = pending || resolvedLegacyMatch;
  const journeyCandidates = normalizeLinkageCandidates(linkage?.candidates);
  const needsLiveCandidates =
    pending && journeyCandidates.length === 0 && Boolean(application?.applicationId);

  const identityQ = useQuery({
    queryKey: ['identityLookup', application?.applicationId],
    queryFn: () =>
      identityVerificationDashboardApi.lookup({
        applicationId: String(application!.applicationId),
      }),
    enabled: Boolean(application?.applicationId) && needsIdentityPii && identityLookupItem === undefined,
    staleTime: 60_000,
  });

  const mappingQ = useQuery({
    queryKey: ['legacyLinkageMapping', application?.applicationId],
    queryFn: () => applicationsApi.getLegacyLinkageMapping(String(application!.applicationId)),
    enabled: needsLiveCandidates,
    staleTime: 15_000,
  });

  const identityRow =
    identityLookupItem !== undefined
      ? identityLookupItem
      : (identityQ.data?.items?.[0] ?? null);

  const identityApplicantPii = useMemo(
    () => resolveLinkageApplicantPiiFromIdentity(application, identityRow),
    [application, identityRow],
  );

  const candidates = useMemo(
    () =>
      mergeLinkageCandidates(
        journeyCandidates,
        normalizeLinkageCandidates(mappingQ.data?.candidates),
      ),
    [journeyCandidates, mappingQ.data?.candidates],
  );

  const candidateCount = pendingReviewCandidateCount(linkage, candidates);
  const canRetry =
    Boolean(application?.applicationId) &&
    status !== 'RESOLVED' &&
    status !== 'PENDING_REVIEW';
  const funding = linkageFundingLabel(linkage);
  const statusColor = linkageStatusChipColor(status);
  const deMismatch = decisionEnginePowerIdMismatch(linkage, decisionEnginePowerId);
  const primaryCandidate = candidates[0] ?? null;
  const primaryReason = primaryCandidate?.piiReason ?? null;

  const reviewHeadline = useMemo(() => {
    if (!pending || !primaryCandidate) return null;
    const reviewApplicant = primaryCandidate.applicantPii ?? null;
    const effectiveApplicant = resolveEffectiveApplicantPii(reviewApplicant, identityApplicantPii);
    const identityDrift =
      Boolean(reviewApplicant) &&
      Boolean(identityApplicantPii) &&
      !piiSnapshotsEqual(reviewApplicant, identityApplicantPii);
    return resolvePendingReviewHeadline({
      applicant: effectiveApplicant,
      legacy: primaryCandidate.legacyPii ?? null,
      identityDrift,
      storedReason: primaryReason,
      reviewApplicantPii: reviewApplicant,
      identityApplicantPii,
    });
  }, [pending, primaryCandidate, identityApplicantPii, primaryReason]);

  const showStatusChip = !(pending && reviewHeadline);

  const linkedLegacyPii = useMemo(() => resolveLinkedLegacyPii(linkage), [linkage]);
  const resolvedIdentityFields = useMemo(
    () => buildLinkageComparisonFields(identityApplicantPii, linkedLegacyPii),
    [identityApplicantPii, linkedLegacyPii],
  );
  const showResolvedIdentity =
    resolvedLegacyMatch &&
    resolvedIdentityFields.some((f) => f.applicant !== '—' || f.legacy !== '—');

  const run = async (fn: () => Promise<unknown>, successMessage?: string) => {
    setBusy(true);
    setError(null);
    setActionNotice(null);
    try {
      await fn();
      await queryClient.invalidateQueries({ queryKey: ['applicationJourney'] });
      await queryClient.invalidateQueries({
        queryKey: ['legacyLinkageMapping', application?.applicationId],
      });
      if (successMessage) setActionNotice(successMessage);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Linkage action failed.');
    } finally {
      setBusy(false);
    }
  };

  const bindPowerId = async (powerId: string) => {
    if (!reviewId) return;
    const ok = window.confirm(LEGACY_LINKAGE_PANEL.bindConfirm(powerId));
    if (!ok) return;
    await run(
      () => applicationsApi.selectLegacyLinkageReview(reviewId, { powerId }),
      LEGACY_LINKAGE_PANEL.bindSuccess(powerId),
    );
  };

  const mintNewId = async () => {
    if (!reviewId) return;
    const ok = window.confirm(LEGACY_LINKAGE_PANEL.mintConfirm);
    if (!ok) return;
    await run(
      () => applicationsApi.rejectAllLegacyLinkageReview(reviewId),
      LEGACY_LINKAGE_PANEL.mintSuccess,
    );
  };

  const refreshCandidates = () => {
    void queryClient.invalidateQueries({ queryKey: ['applicationJourney'] });
    if (application?.applicationId) {
      void queryClient.invalidateQueries({
        queryKey: ['legacyLinkageMapping', application.applicationId],
      });
      if (needsLiveCandidates) void mappingQ.refetch();
    }
  };

  const candidatesLoading = needsLiveCandidates && mappingQ.isFetching && candidates.length === 0;
  const useComparisonCards = candidates.length > 0 && candidates.length <= REVIEW_CARD_MAX;

  const statusIcon =
    status === 'RESOLVED' ? (
      <CheckCircleOutlineRoundedIcon fontSize="small" />
    ) : status === 'UNAVAILABLE' ? (
      <ErrorOutlineRoundedIcon fontSize="small" />
    ) : status === 'PENDING_REVIEW' ? (
      <InfoOutlinedIcon fontSize="small" />
    ) : (
      <LinkRoundedIcon fontSize="small" />
    );

  const sectionIconColor =
    status === 'PENDING_REVIEW'
      ? 'info'
      : statusColor === 'default'
        ? 'primary'
        : statusColor;

  return (
    <ApplicationSectionCard
      title={LEGACY_LINKAGE_PANEL.title}
      subtitle={LEGACY_LINKAGE_PANEL.subtitle}
      icon={<LinkRoundedIcon fontSize="small" />}
      iconColor={sectionIconColor}
      data-testid="application-identity-legacy-linkage"
      endAdornment={
        <Chip
          size="small"
          color={funding.color}
          variant={funding.variant}
          label={funding.label}
          sx={{ height: 24, fontWeight: 700 }}
        />
      }
    >
      <Stack spacing={2} sx={{ width: '100%' }}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          {showStatusChip ? (
            <Chip
              size="small"
              icon={statusIcon}
              label={linkageStatusLabel(status)}
              color={
                status === 'PENDING_REVIEW'
                  ? 'info'
                  : statusColor === 'default'
                    ? undefined
                    : statusColor
              }
              variant={
                status === 'PENDING_REVIEW' || statusColor === 'default' ? 'outlined' : 'filled'
              }
              sx={{ fontWeight: 700 }}
            />
          ) : null}
          {linkage?.powerId ? (
            <Stack direction="row" spacing={0.25} alignItems="center">
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                {LEGACY_LINKAGE_PANEL.legacyCustomerIdLabel}:
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 700 }}>
                {linkage.powerId}
              </Typography>
              <CopyValueButton value={linkage.powerId} />
            </Stack>
          ) : null}
          {linkage?.isMinted ? (
            <Chip size="small" variant="outlined" label="New customer ID" />
          ) : linkage?.powerId ? (
            <Chip size="small" variant="outlined" color="success" label="Legacy match" />
          ) : null}
        </Stack>

        {pending && reviewHeadline ? (
          <Typography variant="body1" sx={{ fontWeight: 600, lineHeight: 1.55 }}>
            {reviewHeadline}
          </Typography>
        ) : null}

        {status === 'RESOLVED' ? (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 1.25,
              p: 1.5,
              borderRadius: 1.5,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                {LEGACY_LINKAGE_PANEL.legacyCustomerIdLabel}
              </Typography>
              <Typography variant="body1" sx={{ fontFamily: 'monospace', fontWeight: 800 }}>
                {linkage?.powerId ?? '—'}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                Customer type
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {linkageSourceLabel(linkage) ?? '—'}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                Match method
              </Typography>
              <Typography variant="body2">{linkageMatchMethodLabel(linkage?.matchMethod) ?? '—'}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                Face distance
              </Typography>
              <Typography variant="body2">{formatLinkageDistance(linkage?.distance) ?? '—'}</Typography>
            </Box>
            {showResolvedIdentity ? (
              <Box sx={{ gridColumn: { xs: '1', sm: '1 / -1' }, pt: 0.5 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontWeight: 800, letterSpacing: '0.04em', display: 'block', mb: 0.75 }}
                >
                  MATCHED IDENTITY
                </Typography>
                <Box
                  sx={{
                    display: { xs: 'none', sm: 'grid' },
                    gridTemplateColumns: '108px 1fr 1fr',
                    gap: 1.25,
                    mb: 0.25,
                  }}
                >
                  <Box />
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                    {LEGACY_LINKAGE_PANEL.columnApplicant}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                    {LEGACY_LINKAGE_PANEL.columnLegacy}
                  </Typography>
                </Box>
                {resolvedIdentityFields.map((field) => (
                  <Box
                    key={field.label}
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', sm: '108px 1fr 1fr' },
                      gap: { xs: 0.25, sm: 1.25 },
                      py: 0.5,
                      borderTop: '1px solid',
                      borderColor: 'divider',
                      '&:first-of-type': { borderTop: 'none' },
                    }}
                  >
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                      {field.label}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {field.applicant}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {field.legacy}
                    </Typography>
                  </Box>
                ))}
              </Box>
            ) : null}
          </Box>
        ) : null}

        {pending ? (
          <Stack spacing={1.5} sx={{ width: '100%' }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
              <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                {LEGACY_LINKAGE_PANEL.reviewSectionTitle}
                {candidateCount != null ? ` (${candidateCount})` : ''}
              </Typography>
              <Button
                size="small"
                color="inherit"
                disabled={busy}
                startIcon={
                  candidatesLoading ? (
                    <CircularProgress size={14} color="inherit" />
                  ) : (
                    <RefreshRoundedIcon fontSize="small" />
                  )
                }
                onClick={refreshCandidates}
              >
                {LEGACY_LINKAGE_PANEL.refresh}
              </Button>
            </Stack>

            {candidatesLoading ? (
              <Stack direction="row" spacing={1} alignItems="center">
                <CircularProgress size={18} />
                <Typography variant="body2" color="text.secondary">
                  Loading candidate details…
                </Typography>
              </Stack>
            ) : null}

            {!candidatesLoading && candidates.length === 0 ? (
              <Alert severity="info">
                {candidateCount != null && candidateCount > 0
                  ? `${candidateCount} candidate${candidateCount === 1 ? ' is' : 's are'} queued but details did not load. Refresh to retry.`
                  : 'No candidates returned yet.'}
                <Box sx={{ mt: 1 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    color="warning"
                    disabled={busy || !reviewId}
                    onClick={() => void mintNewId()}
                  >
                    {LEGACY_LINKAGE_PANEL.assignNewId}
                  </Button>
                </Box>
              </Alert>
            ) : null}

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  md: candidates.length > 1 ? 'repeat(2, minmax(0, 1fr))' : '1fr',
                },
                gap: 2,
                width: '100%',
              }}
            >
              {useComparisonCards
                ? candidates.map((candidate) => (
                    <LegacyLinkageCandidateCard
                      key={candidate.powerId}
                      candidate={candidate}
                      identityApplicantPii={identityApplicantPii}
                      busy={busy}
                      onBind={(powerId) => void bindPowerId(powerId)}
                      onMint={() => void mintNewId()}
                    />
                  ))
                : null}
            </Box>

            {!useComparisonCards && candidates.length > REVIEW_CARD_MAX ? (
              <Box sx={{ overflowX: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Rank</TableCell>
                      <TableCell>{LEGACY_LINKAGE_PANEL.legacyCustomerIdLabel}</TableCell>
                      <TableCell>Distance</TableCell>
                      <TableCell>PII</TableCell>
                      <TableCell align="right">Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {candidates.map((candidate) => (
                      <TableRow key={candidate.powerId} hover>
                        <TableCell>{candidate.rank}</TableCell>
                        <TableCell sx={{ fontFamily: 'monospace' }}>{candidate.powerId}</TableCell>
                        <TableCell>{formatLinkageDistance(candidate.distance) ?? '—'}</TableCell>
                        <TableCell>
                          {piiMatchLabel(candidate.piiCorroborated, candidate.piiReason)}
                        </TableCell>
                        <TableCell align="right">
                          <Button
                            size="small"
                            variant="contained"
                            disabled={busy}
                            onClick={() => void bindPowerId(candidate.powerId)}
                          >
                            Confirm link
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            ) : null}

            {candidates.length > 1 ? (
              <Button
                variant="outlined"
                color="warning"
                disabled={busy || !reviewId}
                onClick={() => void mintNewId()}
                sx={{ alignSelf: 'flex-start' }}
              >
                {LEGACY_LINKAGE_PANEL.noneOfTheseMint}
              </Button>
            ) : null}
          </Stack>
        ) : null}

        {deMismatch ? (
          <Alert severity="warning">
            Decision engine customer ID ({decisionEnginePowerId}) differs from linked legacy ID (
            {linkage?.powerId}). Both systems should agree before funding.
          </Alert>
        ) : null}

        {actionNotice ? <Alert severity="success">{actionNotice}</Alert> : null}
        {error ? <Alert severity="error">{error}</Alert> : null}

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
          {canRetry ? (
            <Button
              variant="outlined"
              disabled={busy}
              onClick={() =>
                run(() => applicationsApi.ensureLegacyLinkage(String(application?.applicationId)))
              }
            >
              {LEGACY_LINKAGE_PANEL.retryResolve}
            </Button>
          ) : null}
          <Button
            size="small"
            color="inherit"
            startIcon={<HelpOutlineRoundedIcon fontSize="small" />}
            onClick={() => setHelpOpen((v) => !v)}
            sx={{ textTransform: 'none' }}
          >
            {helpOpen ? LEGACY_LINKAGE_PANEL.helpToggleHide : LEGACY_LINKAGE_PANEL.helpToggleShow}
          </Button>
        </Stack>
        <Collapse in={helpOpen}>
          <Typography variant="body2" color="text.secondary">
            {LEGACY_LINKAGE_PANEL.helpBody}
          </Typography>
        </Collapse>

        <Box>
          <Button
            size="small"
            color="inherit"
            endIcon={
              <ExpandMoreRoundedIcon
                fontSize="small"
                sx={{ transform: technicalOpen ? 'rotate(180deg)' : undefined, transition: '0.2s' }}
              />
            }
            onClick={() => setTechnicalOpen((v) => !v)}
            sx={{ textTransform: 'none', px: 0, minWidth: 0 }}
          >
            {LEGACY_LINKAGE_PANEL.technicalDetails}
          </Button>
          <Collapse in={technicalOpen}>
            <Stack spacing={0.75} sx={{ mt: 1, pl: 0.5 }}>
              <Typography variant="body2">
                <Box component="span" color="text.secondary">
                  EvolveOS ccid:{' '}
                </Box>
                <Box component="span" sx={{ fontFamily: 'monospace' }}>
                  {application?.ccid ?? '—'}
                </Box>
              </Typography>
              {reviewId ? (
                <Typography variant="body2">
                  <Box component="span" color="text.secondary">
                    Review ID:{' '}
                  </Box>
                  <Box component="span" sx={{ fontFamily: 'monospace' }}>
                    {reviewId}
                  </Box>
                </Typography>
              ) : null}
              <Typography variant="body2">
                <Box component="span" color="text.secondary">
                  Last read:{' '}
                </Box>
                {formatLinkageStamp(linkage?.stampedAt, linkage?.liveRead) ?? '—'}
              </Typography>
            </Stack>
          </Collapse>
        </Box>
      </Stack>
    </ApplicationSectionCard>
  );
}
